import React, { useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL as string;

// In dev the API URL is relative ("/api") — no health check needed locally.
const IS_ABSOLUTE_URL = API_URL?.startsWith('http');

type HealthStatus = 'checking' | 'ok' | 'down';

export function useServiceHealth(): HealthStatus {
  const [status, setStatus] = useState<HealthStatus>(IS_ABSOLUTE_URL ? 'checking' : 'ok');

  useEffect(() => {
    if (!IS_ABSOLUTE_URL) return;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    fetch(`${API_URL}/health`, { signal: controller.signal })
      .then((res) => {
        setStatus(res.ok ? 'ok' : 'down');
      })
      .catch(() => {
        setStatus('down');
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  return status;
}

export const ServiceUnavailablePage: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#0f172a',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}
  >
    <div
      style={{
        maxWidth: 480,
        width: '100%',
        padding: '48px 32px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: '#1e293b',
          border: '2px solid #334155',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 32px',
          fontSize: 28,
        }}
      >
        ⚠
      </div>

      <h1
        style={{
          fontSize: 22,
          fontWeight: 600,
          color: '#f1f5f9',
          margin: '0 0 12px',
          letterSpacing: '-0.02em',
        }}
      >
        Service Unavailable
      </h1>

      <p
        style={{
          fontSize: 15,
          color: '#94a3b8',
          margin: '0 0 32px',
          lineHeight: 1.6,
        }}
      >
        The server could not be reached. It may be temporarily down or undergoing maintenance.
      </p>

      <div
        style={{
          backgroundColor: '#1e293b',
          border: '1px solid #334155',
          borderRadius: 8,
          padding: '16px 20px',
          marginBottom: 32,
          textAlign: 'left',
        }}
      >
        <p
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#64748b',
            margin: '0 0 8px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          What you can do
        </p>
        <ul
          style={{
            fontSize: 14,
            color: '#94a3b8',
            margin: 0,
            paddingLeft: 18,
            lineHeight: 1.8,
          }}
        >
          <li>Wait a moment and try refreshing the page</li>
          <li>Check your internet connection</li>
          <li>Contact support if the problem persists</li>
        </ul>
      </div>

      <button
        onClick={() => window.location.reload()}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 24px',
          backgroundColor: '#3b82f6',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          fontSize: 14,
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'background-color 0.15s',
        }}
        onMouseEnter={(e) => ((e.target as HTMLButtonElement).style.backgroundColor = '#2563eb')}
        onMouseLeave={(e) => ((e.target as HTMLButtonElement).style.backgroundColor = '#3b82f6')}
      >
        Retry
      </button>
    </div>
  </div>
);

export const ServiceCheckingPage: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#0f172a',
    }}
  >
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: 32,
          height: 32,
          border: '3px solid #334155',
          borderTopColor: '#3b82f6',
          borderRadius: '50%',
          margin: '0 auto 16px',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>Connecting to server…</p>
    </div>
  </div>
);
